// src/pages/admin/cashbox/Cashbox.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardBody, Typography } from "@material-tailwind/react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../../firebaseConfig";
import {
  addDoc,
  collection,
  getDocs,
  serverTimestamp,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { TrashIcon, ArrowPathIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { useNavigate, useSearchParams } from "react-router-dom";

const BRAND = {
  ink: "#2F2F2F",
  graphite: "#878787",
  silver: "#B8B8B8",
  mist: "#D0D0D0",
  paper: "#EFEFEF",
  white: "#FCFCFC",
};

export default function Cashbox() {
  const [me, setMe] = useState(null);
  const [role, setRole] = useState(Number(localStorage.getItem("role") || "2"));
  const isAdmin = role === 1;

  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");

  const [kind, setKind] = useState("receipt");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const ui = useMemo(() => {
    return {
      pageBg: `radial-gradient(1200px 520px at 90% -30%, ${BRAND.paper}, ${BRAND.white} 55%)`,
      cardBorder: `1px solid ${BRAND.paper}`,
      cardShadow: "0 18px 50px rgba(0,0,0,0.06), 0 6px 18px rgba(0,0,0,0.04)",
      headerBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      btnBg: `linear-gradient(135deg, ${BRAND.ink}, ${BRAND.graphite})`,
      ring: "0 0 0 4px rgba(135,135,135,0.18)",
    };
  }, []);

  const inputCls =
    "w-full rounded-2xl border px-3 py-3 outline-none transition font-tajawal bg-white";
  const btnPrimary =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition disabled:opacity-60";
  const btnGhost =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-extrabold transition";

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setMe(u || null);
      if (u?.uid) {
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          const r =
            snap.exists() && snap.data().role !== undefined
              ? Number(snap.data().role)
              : Number(localStorage.getItem("role") || "2");
          setRole(r);
          localStorage.setItem("role", String(r));
        } catch {}
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    (async () => {
      if (!me?.uid) return;

      if (isAdmin) {
        try {
          const snap = await getDocs(collection(db, "users"));
          const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
          list.sort((a, b) =>
            String(a.storeName || a.displayName || a.name || a.email || "").localeCompare(
              String(b.storeName || b.displayName || b.name || b.email || ""),
              "ar"
            )
          );

          setUsers(list);

          const urlUid = searchParams.get("uid");
          setSelectedUid(urlUid || list[0]?.id || me.uid);
        } catch {}
      } else {
        setSelectedUid(me.uid);
      }
    })();
    // eslint-disable-next-line
  }, [me, isAdmin]);

  async function load() {
    if (!selectedUid) return;
    setLoading(true);
    setErr("");
    try {
      const snap = await getDocs(collection(db, "users", selectedUid, "cashbox"));
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setRows(list);
    } catch {
      setErr("تعذّر جلب قيود الصندوق.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [selectedUid]);

  const balance = useMemo(() => {
    return rows.reduce((sum, r) => {
      const amt = Number(r.amount || 0);
      return sum + (r.kind === "payment" ? -amt : amt);
    }, 0);
  }, [rows]);

  async function addVoucher() {
    if (!selectedUid) return;

    setErr("");
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setErr("أدخل مبلغًا صالحًا.");
      return;
    }

    try {
      await addDoc(collection(db, "users", selectedUid, "cashbox"), {
        kind,
        source: "manual",
        amount: amt,
        note: note.trim() || "",
        createdAt: serverTimestamp(),
      });
      setAmount("");
      setNote("");
      await load();
    } catch {
      setErr("تعذّر إضافة السند.");
    }
  }

  async function removeRow(id) {
    if (!isAdmin) {
      setErr("ليس لديك صلاحية الحذف (للمشرف فقط).");
      return;
    }
    if (!selectedUid || !id) return;
    try {
      await deleteDoc(doc(db, "users", selectedUid, "cashbox", id));
      await load();
    } catch {
      setErr("تعذّر الحذف.");
    }
  }

  function handleBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  }

  const fmtDate = (ts) => (ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString("ar-IQ") : "-");

  const currentUserName = useMemo(() => {
    if (!isAdmin) return "";
    const u = users.find((x) => x.id === selectedUid);
    return u ? u.storeName || u.displayName || u.name || u.email || u.id : "";
  }, [users, selectedUid, isAdmin]);

  return (
    <div dir="rtl" className="p-4 min-h-screen" style={{ background: ui.pageBg }}>
      <Card
        className="mx-auto max-w-6xl rounded-3xl overflow-hidden bg-white"
        style={{ border: ui.cardBorder, boxShadow: ui.cardShadow }}
      >
        <CardHeader floated={false} shadow={false} className="p-0">
          <div className="flex items-center justify-between px-6 py-6" style={{ background: ui.headerBg }}>
            <button
              onClick={handleBack}
              className="group inline-flex items-center gap-2 rounded-2xl px-3 py-2 transition"
              style={{
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.18)",
                color: BRAND.white,
                fontFamily: "Tajawal, system-ui, sans-serif",
              }}
              title="رجوع"
            >
              <ArrowRightIcon className="h-5 w-5" />
              <span className="text-sm font-extrabold">رجوع</span>
            </button>

            <div className="text-center flex-1">
              <Typography
                variant="h4"
                className="text-white font-extrabold font-tajawal"
                style={{ fontFamily: "Tajawal, system-ui, sans-serif" }}
              >
                الصندوق {isAdmin && currentUserName ? `– ${currentUserName}` : ""}
              </Typography>
              <div className="mt-1 text-xs" style={{ color: "rgba(255,255,255,0.78)" }}>
                إدارة قيود القبض/الصرف ضمن ألوان الصورة فقط
              </div>
            </div>

            <span className="w-[92px]" />
          </div>
        </CardHeader>

        <CardBody className="space-y-6 p-6">
          {err && (
            <div
              className="rounded-2xl px-4 py-3 font-tajawal"
              style={{ background: BRAND.paper, border: `1px solid ${BRAND.mist}`, color: BRAND.ink }}
            >
              {err}
            </div>
          )}

          {/* اختيار المستخدم (أدمن فقط) */}
          {isAdmin && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                  اختر المستخدم
                </label>
                <select
                  value={selectedUid}
                  onChange={(e) => setSelectedUid(e.target.value)}
                  className={inputCls}
                  style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                  onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                  onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                >
                  {users.map((u) => {
                    const name = u.storeName || u.displayName || u.name || u.email || u.id;
                    return (
                      <option key={u.id} value={u.id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="flex gap-2 md:col-span-2 justify-end">
                <button
                  type="button"
                  onClick={load}
                  disabled={loading}
                  className={btnGhost}
                  style={{
                    background: BRAND.white,
                    color: BRAND.ink,
                    border: `1px solid ${BRAND.mist}`,
                    fontFamily: "Tajawal, system-ui, sans-serif",
                  }}
                >
                  <ArrowPathIcon className="h-5 w-5" />
                  تحديث
                </button>
              </div>
            </div>
          )}

          {/* الرصيد */}
          <div
            className="rounded-2xl px-4 py-4 text-lg font-extrabold font-tajawal"
            style={{
              background: BRAND.white,
              border: `1px solid ${BRAND.paper}`,
              color: BRAND.ink,
              boxShadow: "0 10px 24px rgba(0,0,0,0.04)",
            }}
          >
            مجموع الصندوق: {Number(balance).toLocaleString("en-EG")} د.ع
          </div>

          {/* إضافة سند */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                نوع السند
              </label>
              <select
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                className={inputCls}
                style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
              >
                <option value="receipt">سند قبض</option>
                <option value="payment">سند صرف</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                المبلغ
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className={inputCls}
                style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                placeholder="0"
              />
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-extrabold font-tajawal" style={{ color: BRAND.ink }}>
                ملاحظة (اختياري)
              </label>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className={inputCls}
                style={{ borderColor: BRAND.mist, color: BRAND.ink }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = ui.ring)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
                placeholder="..."
              />
            </div>

            <div className="md:col-span-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={addVoucher}
                className={btnPrimary}
                style={{
                  background: ui.btnBg,
                  color: BRAND.white,
                  boxShadow: "0 12px 28px rgba(0,0,0,0.10)",
                  fontFamily: "Tajawal, system-ui, sans-serif",
                }}
                disabled={loading}
              >
                إضافة سند
              </button>

              <button
                type="button"
                onClick={load}
                disabled={loading}
                className={btnGhost}
                style={{
                  background: BRAND.white,
                  color: BRAND.ink,
                  border: `1px solid ${BRAND.mist}`,
                  fontFamily: "Tajawal, system-ui, sans-serif",
                }}
              >
                <ArrowPathIcon className="h-5 w-5" />
                تحديث
              </button>
            </div>
          </div>

          {/* الجدول */}
          <div className="overflow-auto rounded-2xl border" style={{ borderColor: BRAND.paper }}>
            <table className="w-full text-right font-tajawal">
              <thead style={{ backgroundColor: BRAND.paper }}>
                <tr>
                  {["التاريخ", "النوع", "المبلغ", "ملاحظة", "فاتورة", "إجراء"].map((h) => (
                    <th key={h} className="p-3 text-sm font-extrabold" style={{ color: BRAND.ink }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length ? (
                  rows.map((r, idx) => (
                    <tr
                      key={r.id}
                      className="border-t"
                      style={{
                        borderColor: BRAND.paper,
                        background: idx % 2 === 0 ? BRAND.white : "rgba(239,239,239,0.30)",
                      }}
                    >
                      <td className="p-3">{fmtDate(r.createdAt)}</td>
                      <td className="p-3">{r.kind === "payment" ? "سند صرف" : "سند قبض"}</td>
                      <td
                        className="p-3 font-extrabold"
                        style={{ color: r.kind === "payment" ? BRAND.graphite : BRAND.ink }}
                      >
                        {Number(r.amount || 0).toLocaleString("en-EG")} د.ع
                      </td>
                      <td className="p-3">{r.note || "-"}</td>
                      <td className="p-3">{r.invoiceNo ? `#${r.invoiceNo}` : "-"}</td>
                      <td className="p-3">
                        {isAdmin ? (
                          <button
                            className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-extrabold transition"
                            style={{
                              background: BRAND.white,
                              border: `1px solid ${BRAND.mist}`,
                              color: BRAND.ink,
                            }}
                            onClick={() => removeRow(r.id)}
                          >
                            <TrashIcon className="h-5 w-5" />
                            حذف
                          </button>
                        ) : (
                          <span className="text-sm" style={{ color: BRAND.graphite }}>
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="p-6 text-center" style={{ color: BRAND.graphite }}>
                      لا توجد قيود.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
